export type LegalBlock =
  | { t: "p"; text: string }
  | { t: "h"; text: string }
  | { t: "sub"; text: string }
  | { t: "ul"; items: string[] };

export type LegalDocContent = { title: string; effective: string; blocks: LegalBlock[] };

export const TERMS: LegalDocContent = {
  title: "Terms & Conditions",
  effective: "Effective date: June 2026",
  blocks: [
    { t: "p", text: "Welcome to Dost (“Dost”, “we”, “our”, or “us”)." },
    {
      t: "p",
      text: "These Terms of Use (“Terms”) govern your access to and use of the HappiDost.ai platform, including our applications, AI agents, services, and related technologies (collectively, the “Services”). By accessing or using the Services, you agree to be bound by these Terms.",
    },
    { t: "p", text: "If you do not agree to these Terms, you must not use the Services." },

    { t: "h", text: "1. Who We Are" },
    {
      t: "p",
      text: "HappiDost.ai is an AI-powered platform that enables users to interact with AI assistants, AI agents, businesses, and digital services through natural conversations and automated workflows.",
    },

    { t: "h", text: "2. Eligibility" },
    {
      t: "p",
      text: "You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Services. By using the Services, you represent and warrant that you meet these requirements.",
    },

    { t: "h", text: "3. Registration & Account Security" },
    { t: "p", text: "To access certain features, you may be required to create an account. You agree to:" },
    {
      t: "ul",
      items: [
        "Provide accurate and complete information",
        "Maintain the confidentiality of your login credentials",
        "Be responsible for all activities under your account",
      ],
    },
    { t: "p", text: "We are not liable for any loss or damage resulting from unauthorized use of your account." },

    { t: "h", text: "4. Use of Services" },
    { t: "p", text: "You agree to use the Services only for lawful purposes and in accordance with these Terms. You must not:" },
    {
      t: "ul",
      items: [
        "Use the Services for illegal, harmful, or fraudulent activities",
        "Interfere with or disrupt the platform or its infrastructure",
        "Attempt to gain unauthorized access to systems or data",
        "Use AI agents to perform actions without proper authorization",
      ],
    },
    { t: "p", text: "We reserve the right to monitor usage to ensure compliance." },

    { t: "h", text: "5. AI Agents & Automation" },
    { t: "p", text: "Our platform enables AI agents to perform actions based on user instructions. By using these features:" },
    {
      t: "ul",
      items: [
        "You authorize AI agents to act on your behalf",
        "You are responsible for all actions initiated by your agents",
        "You must review and validate automated outputs before relying on them",
      ],
    },
    { t: "p", text: "We do not guarantee the accuracy, completeness, or reliability of AI-generated results." },

    { t: "h", text: "6. User Content" },
    {
      t: "p",
      text: "You retain ownership of any content you submit, upload, or share (“User Content”). By using the Services, you grant us a non-exclusive, worldwide, royalty-free license to:",
    },
    {
      t: "ul",
      items: [
        "Use, process, and display your content to provide the Services",
        "Improve and enhance our platform and AI systems",
      ],
    },
    {
      t: "p",
      text: "You are solely responsible for your content and must ensure it does not violate any laws or third-party rights.",
    },

    { t: "h", text: "7. Third-Party Integrations" },
    { t: "p", text: "The Services may integrate with third-party platforms and services. We are not responsible for:" },
    {
      t: "ul",
      items: [
        "The availability or functionality of third-party services",
        "Any data shared with or processed by third parties",
      ],
    },
    { t: "p", text: "Your use of third-party services is subject to their respective terms and policies." },

    { t: "h", text: "8. Plans, Payments & Billing" },
    { t: "p", text: "Some features may require payment, subscription, or usage-based credits. By purchasing:" },
    {
      t: "ul",
      items: [
        "You agree to pay all applicable fees",
        "Payments are non-refundable unless stated otherwise",
        "We may modify pricing with prior notice",
      ],
    },
    { t: "p", text: "Failure to pay may result in suspension or termination of access." },

    { t: "h", text: "9. Intellectual Property" },
    {
      t: "p",
      text: "All rights, title, and interest in the Services (excluding User Content) are owned by HappiDost.ai or its licensors. This includes:",
    },
    { t: "ul", items: ["Software", "AI models and agents", "Branding, logos, and trademarks"] },
    {
      t: "p",
      text: "You may not copy, modify, distribute, or reverse engineer any part of the Services without permission.",
    },

    { t: "h", text: "10. Suspension & Termination" },
    { t: "p", text: "We may suspend or terminate your access if you:" },
    { t: "ul", items: ["Violate these Terms", "Misuse the platform", "Pose a security or legal risk"] },
    { t: "p", text: "You may stop using the Services at any time." },

    { t: "h", text: "11. Disclaimer of Warranties" },
    { t: "p", text: "The Services are provided on an “as is” and “as available” basis. We make no warranties regarding:" },
    { t: "ul", items: ["Availability or uptime", "Accuracy of AI outputs", "Fitness for a particular purpose"] },
    { t: "p", text: "Use the Services at your own risk." },

    { t: "h", text: "12. Limitation of Liability" },
    { t: "p", text: "To the fullest extent permitted by law, HappiDost.ai shall not be liable for:" },
    {
      t: "ul",
      items: ["Indirect, incidental, or consequential damages", "Loss of data, profits, or business opportunities"],
    },
    { t: "p", text: "Our total liability shall not exceed the amount paid by you (if any) for the Services." },

    { t: "h", text: "13. Indemnification" },
    {
      t: "p",
      text: "You agree to indemnify and hold harmless HappiDost.ai from any claims, damages, or liabilities arising from:",
    },
    { t: "ul", items: ["Your use of the Services", "Your violation of these Terms", "Your User Content"] },

    { t: "h", text: "14. Dispute Resolution" },
    {
      t: "p",
      text: "Any disputes arising from these Terms shall be resolved in accordance with applicable laws. Jurisdiction and governing law will be determined based on the company's registered location unless otherwise specified.",
    },

    { t: "h", text: "15. Changes to These Terms" },
    { t: "p", text: "We may update these Terms from time to time. We will notify users of material changes through:" },
    { t: "ul", items: ["Platform notifications", "Email (if applicable)"] },
    { t: "p", text: "Continued use of the Services after changes constitutes acceptance of the updated Terms." },

    { t: "h", text: "16. Contact Us" },
    { t: "p", text: "If you have any questions about these Terms, please contact us at:" },
    { t: "ul", items: ["Email: support@happidost.ai", "Website: www.happidost.ai"] },
    {
      t: "p",
      text: "By using HappiDost.ai, you acknowledge that you have read, understood, and agree to these Terms of Use.",
    },
  ],
};

export const PRIVACY: LegalDocContent = {
  title: "Privacy Policy",
  effective: "Effective date: June 2026",
  blocks: [
    {
      t: "p",
      text: "At Dost (“Dost”, “we”, “our”, or “us”), we respect your privacy and are committed to protecting your personal information.",
    },
    {
      t: "p",
      text: "This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the HappiDost.ai platform, applications, AI agents, and related services (collectively, the “Services”).",
    },
    { t: "p", text: "By accessing or using our Services, you agree to the terms of this Privacy Policy." },

    { t: "h", text: "1. Information We Collect" },
    { t: "p", text: "We collect the following types of information:" },
    { t: "sub", text: "a. Information You Provide" },
    {
      t: "ul",
      items: [
        "Name, email address, phone number",
        "Account credentials",
        "Messages, prompts, and content shared with AI agents",
        "Payment and billing information",
      ],
    },
    { t: "sub", text: "b. Automatically Collected Information" },
    {
      t: "ul",
      items: [
        "Device information (IP address, browser type, OS)",
        "Usage data (features used, interactions, logs)",
        "Cookies and tracking technologies",
      ],
    },
    { t: "sub", text: "c. AI Interaction Data" },
    {
      t: "ul",
      items: [
        "Text conversations with AI agents",
        "Voice inputs and audio interactions (if enabled)",
        "Generated responses and automation logs",
      ],
    },

    { t: "h", text: "2. How We Use Your Information" },
    { t: "p", text: "We use your information to:" },
    {
      t: "ul",
      items: [
        "Provide, operate, and maintain the Services",
        "Process user requests and AI interactions",
        "Improve AI models, features, and performance",
        "Personalize user experience",
        "Ensure platform security and prevent fraud",
        "Communicate updates, support, and service-related notices",
      ],
    },

    { t: "h", text: "3. AI Conversations & Voice Data" },
    { t: "p", text: "Your interactions with AI agents may be processed and stored to:" },
    {
      t: "ul",
      items: ["Deliver accurate and contextual responses", "Improve system performance and automation quality"],
    },
    { t: "p", text: "Voice data (if used):" },
    {
      t: "ul",
      items: [
        "May be temporarily processed or stored for transcription and response generation",
        "Is handled with strict security controls",
      ],
    },
    {
      t: "p",
      text: "You should avoid sharing sensitive personal or confidential information in AI interactions unless necessary.",
    },

    { t: "h", text: "4. Cookies & Tracking Technologies" },
    { t: "p", text: "We use cookies and similar technologies to:" },
    {
      t: "ul",
      items: [
        "Maintain sessions and authentication",
        "Understand user behavior and usage patterns",
        "Improve performance and user experience",
      ],
    },
    { t: "p", text: "You can control cookie settings through your browser." },

    { t: "h", text: "5. Third-Party Integrations" },
    {
      t: "p",
      text: "Our Services may connect with third-party platforms (e.g., APIs, business tools, external services). When using such integrations:",
    },
    {
      t: "ul",
      items: ["Data may be shared with those services as required", "Their privacy policies will apply to that data"],
    },
    { t: "p", text: "We are not responsible for third-party privacy practices." },

    { t: "h", text: "6. Data Sharing & Disclosure" },
    { t: "p", text: "We may share your information:" },
    {
      t: "ul",
      items: [
        "With service providers and infrastructure partners (hosting, analytics, payments)",
        "To comply with legal obligations or government requests",
        "To enforce our Terms of Use",
        "To protect rights, safety, and security",
      ],
    },
    { t: "p", text: "We do not sell your personal data." },

    { t: "h", text: "7. Data Retention" },
    { t: "p", text: "We retain your information:" },
    {
      t: "ul",
      items: [
        "For as long as your account is active",
        "As necessary to provide the Services",
        "To comply with legal, tax, or regulatory requirements",
      ],
    },
    { t: "p", text: "You may request deletion of your data (subject to legal obligations)." },

    { t: "h", text: "8. Your Privacy Rights" },
    { t: "p", text: "Depending on your jurisdiction, you may have the right to:" },
    {
      t: "ul",
      items: [
        "Access your personal data",
        "Correct inaccurate data",
        "Request deletion of your data",
        "Restrict or object to certain processing",
        "Withdraw consent where applicable",
      ],
    },
    { t: "p", text: "To exercise your rights, contact us at the details below." },

    { t: "h", text: "9. Privacy Controls" },
    { t: "p", text: "We provide tools and settings to:" },
    {
      t: "ul",
      items: [
        "Manage your account information",
        "Control notifications and communications",
        "Delete or modify content (where applicable)",
      ],
    },

    { t: "h", text: "10. Children's Privacy" },
    {
      t: "p",
      text: "Our Services are not intended for individuals under the age of 18 (or applicable age of majority). We do not knowingly collect data from children. If we become aware of such data, we will delete it promptly.",
    },

    { t: "h", text: "11. Data Security" },
    { t: "p", text: "We implement industry-standard measures to protect your information, including:" },
    {
      t: "ul",
      items: [
        "Encryption in transit and at rest",
        "Access controls and authentication mechanisms",
        "Monitoring and security practices",
      ],
    },
    { t: "p", text: "However, no system is completely secure, and we cannot guarantee absolute security." },

    { t: "h", text: "12. International Data Transfers" },
    {
      t: "p",
      text: "Your information may be processed and stored in locations outside your country. By using our Services, you consent to such transfers, subject to applicable data protection laws.",
    },

    { t: "h", text: "13. Changes to This Privacy Policy" },
    { t: "p", text: "We may update this Privacy Policy from time to time. We will notify you of significant changes through:" },
    { t: "ul", items: ["Platform notifications", "Email communication (if applicable)"] },
    { t: "p", text: "Continued use of the Services after updates constitutes acceptance of the revised policy." },

    { t: "h", text: "14. Contact Us" },
    { t: "p", text: "If you have any questions or requests regarding this Privacy Policy, please contact us:" },
    { t: "ul", items: ["Email: support@happidost.ai", "Website: www.happidost.ai"] },
    { t: "p", text: "By using Dost, you acknowledge that you have read and understood this Privacy Policy." },
  ],
};
